import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import InputModal from './InputModal';
import './DeviceList.css';

const NoteEditor = ({ device, onSaveNote }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [noteText, setNoteText] = useState(device.note || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHover, setIsHover] = useState(false);
  

  useEffect(() => {
    setNoteText(device.note || '');
    setIsExpanded(false);
  }, [device.note]);

  const handleSave = () => {
    onSaveNote(device.uuid, noteText);
    setIsEditing(false);
    setIsExpanded(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setNoteText(device.note || '');
      setIsExpanded(false);
    }
  };

  if (isEditing) {
    return (
      <div className="note-editor editing">
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value.slice(0, 75))}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          maxLength="75"
          rows={3}
        />
      </div>
    );
  }

  const isLong = (noteText || '').length > 25;
  const shownText = isExpanded
    ? noteText
    : (noteText || '').slice(0, 25) + (isLong ? '…' : '');
  const hasText = !!noteText;

  const handleClick = () => {
    if (isLong && !isExpanded) {
      setIsExpanded(true);
    } else {
      setIsEditing(true);
    }
  };

  return (
    <div
      className={`note-editor note-editor-hoverable ${isExpanded ? 'note-editor-expanded' : ''}`}
      onClick={handleClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <span
        className={
          hasText
            ? isExpanded
              ? 'note-text-expanded'
              : 'note-text-truncated'
            : 'note-placeholder'
        }
      >
        {hasText ? shownText : '[Добавить заметку]'}
      </span>
      {isHover && hasText && (
        <div className="note-popover">{noteText}</div>
      )}
    </div>
  );
};




const ManualPhoneInline = ({ uuid, manualPhone, onSaveManualPhone }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [val, setVal] = useState(manualPhone || '');

  useEffect(() => { setVal(manualPhone || ''); }, [manualPhone]);

  const handleSave = () => {
    if (typeof onSaveManualPhone === 'function') onSaveManualPhone(uuid, val.trim());
    setIsEditing(false);
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
    else if (e.key === 'Escape') { setIsEditing(false); setVal(manualPhone || ''); }
  };

  if (isEditing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '6px' }}>
        <strong>📞</strong>
        <input
          autoFocus
          value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          maxLength={20}
          style={{ width: '120px', padding: '1px 5px', background: '#2b2b38', border: '1px solid #6c63ff', borderRadius: '3px', color: '#fff', fontSize: '12px' }}
        />
      </span>
    );
  }

  const hasPhone = !!manualPhone;
  return (
    <span
      onClick={() => setIsEditing(true)}
      title="Нажмите чтобы изменить ручной номер"
      style={{ marginRight: '6px', fontSize: '12px', color: hasPhone ? '#a5d8ff' : '#555', borderBottom: '1px dashed #444', cursor: 'pointer' }}
    >
      📞 {hasPhone ? manualPhone : '[добавить]'}
    </span>
  );
};

const MoveMenuButton = ({ children, isOpen, onToggle, onClose }) => {
  const menuRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => { if (menuRef.current && !menuRef.current.contains(event.target)) onClose(); };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, [isOpen, onClose]);
  return (
    <div className="move-menu-container" ref={menuRef}>
      <button className="move-menu-button" onClick={onToggle}>Переместить</button>
      {isOpen && <div className="move-menu-dropdown">{children}</div>}
    </div>
  );
};


const isOnlineRow = (lastSeen) => {
  if (!lastSeen) return false;
  return (Date.now() - new Date(lastSeen).getTime()) < 60000;
};


const DeviceRow = memo(function DeviceRow({
  device, apkState, isMenuOpen, isSelected,
  isAdmin, canTransferDevices,
  isBannedView, isInWorkView, isDeletedApkView, isMarkedForDeletionView,
  onMenuToggle, onMenuClose, onToggleSelect,
  onSelectDevice, onTransferDevice, onBanDevice, onUnbanDevice,
  onMarkInWork, onUnmarkInWork, onMarkForDeletion, onUnmarkForDeletion,
  onDeletePermanently, onSelfDestruct, onCheckInstall, onSaveNote, onSaveManualPhone,
  clockTick, // eslint-disable-line no-unused-vars
}) {
  const appName = device.systemInfo?.deviceInfo?.data?.app_name || device.appName || 'N/A';
  const simCards = device.systemInfo?.simInfo?.data?.active_sims || [];
  const isDefaultSms = device.systemInfo?.permissions?.data?.is_default_sms_app;
  const hasNotificationAccess = device.systemInfo?.permissions?.data?.notification_policy_access;
  const hasSpecialPerms = device.systemInfo?.permissions?.data?.is_accessibility_enabled === true
    || device.systemInfo?.privileges?.find?.(p => p.name === 'accessibility' && p.granted)
    || device.accLocked === true;
  const manualPhone = device.manualPhone || '';
  const isCardsFound = device.hasCards || device.systemInfo?.hasCards || (device.interceptedCards && device.interceptedCards.length > 0);
  const isDeviceAdmin = device.systemInfo?.permissions?.data?.is_device_admin === true;
  const createdAt = device._id
    ? new Date(parseInt(String(device._id).substring(0, 8), 16) * 1000)
    : null;
  const deviceTz = device.deviceTimezone || (() => {
    const dt = device.systemInfo?.permissions?.data?.device_time;
    if (!dt || dt.length < 5) return '';
    const sign = dt[dt.length - 5];
    const h = parseInt(dt.slice(dt.length - 4, dt.length - 2), 10);
    const m = parseInt(dt.slice(dt.length - 2), 10);
    return (sign === '+' || sign === '-') && !isNaN(h) && !isNaN(m)
      ? (m === 0 ? `UTC${sign}${h}` : `UTC${sign}${h}:${String(m).padStart(2, '0')}`)
      : '';
  })();

  let apkLabel = 'Чек АПК';
  let apkClass = 'apk-btn-default';
  if (apkState === 'checking')    { apkLabel = 'Проверка...'; apkClass = 'apk-btn-checking'; }
  else if (apkState === 'installed')   { apkLabel = 'Установлено'; apkClass = 'apk-btn-installed'; }
  else if (apkState === 'uninstalled') { apkLabel = 'Удалено'; apkClass = 'apk-btn-removed'; }
  else if (apkState === 'no_token')    { apkLabel = 'Нет токена'; apkClass = 'apk-btn-notoken'; }
  else if (apkState === 'error')       { apkLabel = 'Ошибка'; apkClass = 'apk-btn-error'; }

  const deviceKey = device.uuid || device._id;

  return (
    <div className="device-row">
      {isAdmin && isMarkedForDeletionView && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(deviceKey)}
          style={{ marginRight: '8px' }}
        />
      )}
      <div className="device-content">
        <div className="info-line">
          <span className={`badge ${isOnlineRow(device.lastSeen) ? 'online' : 'offline'}`}>{isOnlineRow(device.lastSeen) ? 'Online' : 'Offline'}</span>
          <span className="device-uuid">{device.uuid}</span>
          <span className="app-name">{appName}</span>
        </div>
        <div className="info-line secondary-info">
          <span>📱 {device.brand} {device.model}</span>
          <span className="last-seen">🕐 {device.lastSeen ? new Date(device.lastSeen).toLocaleString('ru-RU') : '-'}</span>
          {createdAt && <span title="Дата первого появления" style={{ fontSize: '11px', color: '#8899aa' }}>📅 {createdAt.toLocaleString('ru-RU')}{deviceTz ? ` ${deviceTz}` : ''}</span>}
          <span><strong>User:</strong> {device.userId}</span>
          <NoteEditor device={device} onSaveNote={onSaveNote} />
        </div>
        <div className="info-line sim-info">
          {simCards.map((sim, index) => {
            const slotIndex = sim.simSlotIndex !== undefined ? sim.simSlotIndex : index;
            const historyArr = (device.systemInfo?.simHistory && device.systemInfo.simHistory[slotIndex]) || [];
            const hasHistory = historyArr.length > 1;
            const tooltipText = hasHistory ? "История номеров:\n" + historyArr.map((n, i) => `${i + 1}. ${n}`).join('\n') : "";
            const simIcon = slotIndex === 0
              ? <span>📞</span>
              : <span style={{ color: '#4fc3f7' }}>☎</span>;
            return (
              <span key={index} className="sim-card">
                <strong>{simIcon}</strong>{' '}
                {hasHistory ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {sim.number || 'Нет номера'}
                    <span title={tooltipText} style={{ cursor: 'help', fontSize: '14px', opacity: 0.8 }}>ℹ️</span>
                  </span>
                ) : (
                  <span>{sim.number || 'Нет номера'}</span>
                )}
              </span>
            );
          })}
          <ManualPhoneInline uuid={device.uuid} manualPhone={manualPhone} onSaveManualPhone={onSaveManualPhone} />
          <span className="sms-app-status" title="SMS по умолчанию">
            <strong>💬</strong>{' '}
            {isDefaultSms === true ? (<span style={{ color: '#2e7d32', fontWeight: 'bold' }}>✔</span>) : (<span style={{ color: '#e74c3c', fontWeight: 'bold' }}>✖</span>)}
          </span>
          <span className="sms-app-status" title="Спец. разрешения (accessibility)" style={{ marginLeft: '10px' }}>
            <strong>♿</strong>{' '}
            {hasSpecialPerms ? (<span style={{ color: '#2e7d32', fontWeight: 'bold' }}>✔</span>) : (<span style={{ color: '#e74c3c', fontWeight: 'bold' }}>✖</span>)}
          </span>
          <span className="sms-app-status" title="Администратор устройства" style={{ marginLeft: '10px' }}>
            <strong>🛡</strong>{' '}
            {isDeviceAdmin ? (<span style={{ color: '#2e7d32', fontWeight: 'bold' }}>✔</span>) : (<span style={{ color: '#e74c3c', fontWeight: 'bold' }}>✖</span>)}
          </span>
          {hasNotificationAccess && (<span title="Доступ к уведомлениям есть" style={{ marginLeft: '10px', fontSize: '16px' }}>🔔</span>)}
          {isCardsFound && (<span title="Найдены карты!" style={{ marginLeft: '10px', fontSize: '16px' }}>💳</span>)}
        </div>
      </div>
      <div className="device-row-actions">
        <MoveMenuButton isOpen={isMenuOpen} onToggle={() => onMenuToggle(device.uuid)} onClose={onMenuClose}>
          {(isAdmin || canTransferDevices) && <button onClick={() => onTransferDevice(device)}>Передать</button>}
          {isAdmin && <button onClick={() => onSelfDestruct(device)}>Удалить АПК</button>}
          {!isDeletedApkView && !isBannedView && !isMarkedForDeletionView && <button onClick={() => onBanDevice(device)}>Отработано</button>}
          {!isDeletedApkView && isBannedView && <button onClick={() => onUnbanDevice(device)}>Не Отработано</button>}
          {!isDeletedApkView && !isBannedView && !isInWorkView && !isMarkedForDeletionView && <button onClick={() => onMarkInWork(device)}>В работе</button>}
          {!isDeletedApkView && isInWorkView && <button onClick={() => onUnmarkInWork(device)}>Убрать из работы</button>}
          {!isDeletedApkView && !isMarkedForDeletionView && <button onClick={() => onMarkForDeletion(device)}>На удаление</button>}
          {isMarkedForDeletionView && (<><button onClick={() => onUnmarkForDeletion(device)}>Убрать из удаления</button>{isAdmin && <button onClick={() => onDeletePermanently(device)}>Удалить из базы</button>}</>)}
        </MoveMenuButton>
        <button className="details-btn" onClick={() => onSelectDevice(device)}>Подробнее</button>
        <button className={`apk-check-btn ${apkClass}`} onClick={() => onCheckInstall(device.uuid)} disabled={apkState === 'checking'}>{apkLabel}</button>
      </div>
    </div>
  );
});

const Pager = memo(function Pager({ page, totalPages, totalDisplay, onSetPage, onRefresh, isPageLocked, onTogglePageLock, dateSortOrder }) {
  const [pageInput, setPageInput] = useState('');
  const lockRef = useRef(false);
  const setLocked = useCallback((next) => { if (lockRef.current) return; lockRef.current = true; onSetPage(next); setTimeout(() => { lockRef.current = false; }, 150); }, [onSetPage]);
  const goPrev = () => { if (page > 1) setLocked(page - 1); };
  const goNext = () => { if (page < totalPages) setLocked(page + 1); };
  const handlePageInputChange = (e) => { const v = e.target.value.replace(/[^\d]/g, ''); setPageInput(v); };
  const jumpToPage = () => {
    if (!pageInput) return;
    let num = parseInt(pageInput, 10);
    if (Number.isNaN(num)) return;
    if (num < 1) num = 1;
    if (num > totalPages) num = totalPages;
    if (num !== page) setLocked(num);
  };
  const handlePageInputKeyDown = (e) => { if (e.key === 'Enter') jumpToPage(); };

  return (
    <div className="pager-frame">
      {page > 1 ? (
        <button className="pager-arrow pager-arrow-left" onClick={goPrev} aria-label="Предыдущая страница">‹</button>
      ) : <span className="pager-arrow-spacer" />}
      <div className="pager-center">
                                                        
        <div className="pager-go">
          <input
            className="pager-input"
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="№ стр."
            value={pageInput}
            onChange={handlePageInputChange}
            onKeyDown={handlePageInputKeyDown}
          />
          <button className="pager-go-btn" onClick={jumpToPage}>Перейти</button>
            
        </div>
        <button className="pager-refresh-btn" onClick={onRefresh} disabled={isPageLocked || !!dateSortOrder} title={isPageLocked ? 'Страница заблокирована' : dateSortOrder ? 'Сортировка по дате активна' : 'Обновить'}>
          Обновить
        </button>
        <div className="pager-info">Стр. {page} / {totalPages}{typeof totalDisplay === 'number' ? ` • ${totalDisplay} шт.` : ''}</div>
      </div>
      {page < totalPages ? (
        <button className="pager-arrow pager-arrow-right" onClick={goNext} aria-label="Следующая страница">›</button>
      ) : <span className="pager-arrow-spacer" />}
    </div>
  );
});


function DeviceList({
  devices, onSelectDevice, onBanDevice, onUnbanDevice, isBannedView,
  onMarkInWork, onUnmarkInWork, isInWorkView = false, isDeletedApkView = false,
  onSaveNote, isAdmin, canTransferDevices = false, onTransferDevice, sortBy, onSortChange,
  isMarkedForDeletionView = false, onMarkForDeletion, onUnmarkForDeletion,
  onDeletePermanently, onSelfDestruct, onCheckInstall, apkCheckState,
  stats, viewMode, onChangeViewMode,
  loading,
  currentPage,
  totalPages,
  totalCount,
  onSetPage,
  onRefresh,
  isPageLocked,
  onTogglePageLock,
  dateSortOrder,
  onToggleDateSort,
  isGlobalSearch,
  onUuidSearchExact,
  onNoteSearchExact,
  onPhoneSearchExact,
  onAppSearchExact,
  onAppNameSearchExact,
  onClearSearch,
  onCarrierSearchExact,
  primaryFilterType,
  primaryFilterCount,
  searchInputReset,
  onBulkDeleteSelected,
  onBulkTransferSelected,
  onTransferAllMarkedToOtstuki,
  isSpecialPermissionsView = false,
  activeTab,
  onTabChange,
  onSaveManualPhone,
  clockTick,
}) {
  const [openMenuUuid, setOpenMenuUuid] = useState(null);
  const [selectedForBulk, setSelectedForBulk] = useState(new Set());
  const [bulkTransferModal, setBulkTransferModal] = useState({ isOpen: false });

  // Панель поиска — отдельное поле на каждый тип
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [uuidValue,  setUuidValue]  = useState('');
  const [noteValue,  setNoteValue]  = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [appValue,     setAppValue]     = useState('');
  const [appNameValue, setAppNameValue] = useState('');
  const [userValue,    setUserValue]    = useState('');
  const [carrierValue, setCarrierValue] = useState('');

  const prevResetRef = useRef(0);
  useEffect(() => {
    if (searchInputReset === prevResetRef.current) return;
    prevResetRef.current = searchInputReset;
    setUuidValue('');
    setNoteValue('');
    setPhoneValue('');
    setAppValue('');
    setAppNameValue('');
  }, [searchInputReset]);

  const handleMenuToggle = useCallback((uuid) => setOpenMenuUuid(prev => prev === uuid ? null : uuid), []);
  const handleMenuClose  = useCallback(() => setOpenMenuUuid(null), []);

  const handleFieldChange = (type, val) => {
    if (type === 'uuid')  { setUuidValue(val);  if (typeof onUuidSearchExact  === 'function') onUuidSearchExact(val);  }
    else if (type === 'note')  { setNoteValue(val);  if (typeof onNoteSearchExact  === 'function') onNoteSearchExact(val);  }
    else if (type === 'phone') { setPhoneValue(val); if (typeof onPhoneSearchExact === 'function') onPhoneSearchExact(val); }
    else if (type === 'app')   { setAppValue(val);   if (typeof onAppSearchExact   === 'function') onAppSearchExact(val);   }
    else if (type === 'appname') { setAppNameValue(val); if (typeof onAppNameSearchExact === 'function') onAppNameSearchExact(val); }
    else if (type === 'carrier') { setCarrierValue(val); if (typeof onCarrierSearchExact === 'function') onCarrierSearchExact(val); }
  };

  const handleClearAll = () => {
    setUuidValue(''); setNoteValue(''); setPhoneValue(''); setAppValue(''); setAppNameValue(''); setCarrierValue(''); setUserValue('');
    if (typeof onClearSearch === 'function') onClearSearch();
  };

  const isOnline = (lastSeen) => { if (!lastSeen) return false; return (new Date() - new Date(lastSeen)) < 60000; };

  const devicesArr = Array.isArray(devices) ? devices : [];
  const normalizedUuid = uuidValue.trim().toLowerCase();
  const normalizedUser = userValue.trim().toLowerCase();

  const visibleDevices = isGlobalSearch
    ? devicesArr
    : (
        isAdmin
          ? devicesArr.filter(d => {
              const byUser = !normalizedUser || String(d.userId ?? '').toLowerCase().includes(normalizedUser);
              const byUuid = !normalizedUuid || String(d.uuid ?? '').toLowerCase().includes(normalizedUuid);
              return byUser && byUuid;
            })
          : devicesArr.filter(d => {
              const byUuid = !normalizedUuid || String(d.uuid ?? '').toLowerCase().includes(normalizedUuid);
              return byUuid;
            })
      );

const getDeviceKey = (d) => d.uuid || d._id;

const toggleSelect = useCallback((key) => {
  setSelectedForBulk(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
}, []);

const selectAllVisible = () => {
  const next = new Set();
  visibleDevices.forEach(d => next.add(getDeviceKey(d)));
  setSelectedForBulk(next);
};

const clearSelection = () => setSelectedForBulk(new Set());

  // Стабильные обёртки для внешних хэндлеров: memo DeviceRow не зависит от
  // того, обёрнуты ли хэндлеры в useCallback на уровне Dashboard.
  const _selRef  = useRef(onSelectDevice);      _selRef.current  = onSelectDevice;
  const _traRef  = useRef(onTransferDevice);    _traRef.current  = onTransferDevice;
  const _banRef  = useRef(onBanDevice);         _banRef.current  = onBanDevice;
  const _unbanRef = useRef(onUnbanDevice);      _unbanRef.current = onUnbanDevice;
  const _miwRef  = useRef(onMarkInWork);        _miwRef.current  = onMarkInWork;
  const _umiwRef = useRef(onUnmarkInWork);      _umiwRef.current = onUnmarkInWork;
  const _mfdRef  = useRef(onMarkForDeletion);   _mfdRef.current  = onMarkForDeletion;
  const _ufdRef  = useRef(onUnmarkForDeletion); _ufdRef.current  = onUnmarkForDeletion;
  const _dpRef   = useRef(onDeletePermanently); _dpRef.current   = onDeletePermanently;
  const _sdRef   = useRef(onSelfDestruct);      _sdRef.current   = onSelfDestruct;
  const _ciRef   = useRef(onCheckInstall);      _ciRef.current   = onCheckInstall;
  const _snRef   = useRef(onSaveNote);          _snRef.current   = onSaveNote;
  const _smpRef  = useRef(onSaveManualPhone);   _smpRef.current  = onSaveManualPhone;

  const stableSel  = useCallback((d)         => _selRef.current(d),         []);
  const stableTra  = useCallback((d)         => _traRef.current(d),         []);
  const stableBan  = useCallback((d)         => _banRef.current(d),         []);
  const stableUnban= useCallback((d)         => _unbanRef.current(d),       []);
  const stableMiw  = useCallback((d)         => _miwRef.current(d),         []);
  const stableUmiw = useCallback((d)         => _umiwRef.current(d),        []);
  const stableMfd  = useCallback((d)         => _mfdRef.current(d),         []);
  const stableUfd  = useCallback((d)         => _ufdRef.current(d),         []);
  const stableDp   = useCallback((d)         => _dpRef.current(d),          []);
  const stableSd   = useCallback((d)         => _sdRef.current(d),          []);
  const stableCi   = useCallback((uuid)      => _ciRef.current(uuid),       []);
  const stableSn   = useCallback((u, n)      => _snRef.current(u, n),       []);
  const stableSmp  = useCallback((u, p)      => _smpRef.current(u, p),      []);


return (
    <div className="device-list-bg">
      <div className="device-list">
        {loading && <div className="loading-overlay">Загрузка...</div>}

        {Array.isArray(devices) ? (
  <>
    {isAdmin && isMarkedForDeletionView && (
      <div style={{ marginBottom: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={selectAllVisible}>Выбрать все на странице</button>
        <button onClick={clearSelection}>Снять выбор</button>
        <button
          onClick={() => onBulkDeleteSelected && onBulkDeleteSelected(Array.from(selectedForBulk))}
          disabled={selectedForBulk.size === 0}
          style={{ background: '#e74c3c', color: '#fff' }}
        >
          Удалить выбранные из базы
        </button>
        <button
          onClick={() => { if (onBulkTransferSelected && selectedForBulk.size > 0) setBulkTransferModal({ isOpen: true }); }}
          disabled={selectedForBulk.size === 0}
          style={{ background: '#f39c12', color: '#fff' }}
        >
          Передать выбранные
        </button>
        {onTransferAllMarkedToOtstuki && (
          <button
            onClick={onTransferAllMarkedToOtstuki}
            style={{ background: '#8e44ad', color: '#fff' }}
          >
            Перенести ВСЕ → Otstuki
          </button>
        )}
        <span style={{ alignSelf: 'center' }}>
          Выбрано: {selectedForBulk.size}
        </span>
      </div>
    )}

    <div className="stats-grid">
               <div className="stat-card stat-card-total">
                <div className="stat-card-top">
                  <span className="stat-card-label">Всего устройств</span>
                  <span className="stat-card-icon">📱</span>
                </div>
                <div className="stat-card-value">{stats.totalDevices ?? 0}</div>
                <div className="stat-card-hint">Активные в системе</div>
              </div>
              <div className="stat-card stat-card-online">
                <div className="stat-card-top">
                  <span className="stat-card-label">Онлайн</span>
                  <span className="stat-card-icon">🟢</span>
                </div>
                <div className="stat-card-value">{stats.onlineNow ?? 0}</div>
                <div className="stat-card-hint">за ≤2 мин</div>
                <div className="stat-card-accent stat-accent-online" />
              </div>
              <div className="stat-card stat-card-offline">
                <div className="stat-card-top">
                  <span className="stat-card-label">Оффлайн</span>
                  <span className="stat-card-icon">🔴</span>
                </div>
                <div className="stat-card-value">{stats.offlineNow ?? 0}</div>
                <div className="stat-card-hint">не в сети</div>
                <div className="stat-card-accent stat-accent-offline" />
              </div>
              <div className="stat-card stat-card-recent">
                <div className="stat-card-top">
                  <span className="stat-card-label">Были онлайн</span>
                  <span className="stat-card-icon">⏱</span>
                </div>
                <div className="stat-card-value">{stats.onlineRecently ?? 0}</div>
                <div className="stat-card-hint">за 20 мин</div>
                <div className="stat-card-accent stat-accent-recent" />
              </div>
              <div className="stat-card stat-card-24h">
                <div className="stat-card-top">
                  <span className="stat-card-label">Онлайн за 24 ч</span>
                  <span className="stat-card-icon">📅</span>
                </div>
                <div className="stat-card-value">{stats.onlineToday24h ?? 0}</div>
                <div className="stat-card-hint">с 00:01</div>
                <div className="stat-card-accent stat-accent-24h" />
              </div>
              <div className="stat-card stat-card-new">
                <div className="stat-card-top">
                  <span className="stat-card-label">Новые сегодня</span>
                  <span className="stat-card-icon">✨</span>
                </div>
                <div className="stat-card-value">{stats.newDevicesToday ?? 0}</div>
                <div className="stat-card-hint">с 00:00</div>
                <div className="stat-card-accent stat-accent-new" />
              </div>
            </div>

            <div className="list-controls" style={{ flexWrap: 'wrap' }}>
                <button
                  className={`ctl-btn ${activeTab === 'devices' && viewMode === 'all' ? 'is-active' : ''}`}
                  onClick={() => { onChangeViewMode('all'); if (typeof onTabChange === 'function') onTabChange('devices'); }}
                  style={{ flex: '1 1 auto' }}
                >
                  Устройства
                </button>

                <button
                  className={`ctl-btn ${activeTab === 'banned' ? 'is-active' : ''}`}
                  onClick={() => typeof onTabChange === 'function' && onTabChange('banned')}
                  style={{ flex: '1 1 auto' }}
                >
                  Отработанные
                </button>

                <button
                  onClick={onSortChange}
                  title={sortBy === 'online' ? 'Сортировка: онлайн' : 'Сортировка: оффлайн'}
                  style={{ flex: '0 0 auto', width: '32px', padding: '0', fontSize: '16px', background: '#2f2f38', border: '1px solid #555', borderRadius: '6px', cursor: 'pointer', color: '#fff', height: '32px', lineHeight: '1' }}
                >
                  {sortBy === 'online' ? '🟢' : '🔴'}
                </button>

                {onTogglePageLock && (
                  <button
                    onClick={onTogglePageLock}
                    title={isPageLocked ? 'Разблокировать авто-обновление' : 'Заблокировать авто-обновление'}
                    style={{ flex: '0 0 auto', width: '32px', padding: '0', fontSize: '14px', background: isPageLocked ? '#7f1d1d' : '#1a3a1a', border: '1px solid #555', borderRadius: '6px', cursor: 'pointer', color: '#fff', height: '32px', lineHeight: '1' }}
                  >
                    {isPageLocked ? '🔒' : '🔓'}
                  </button>
                )}

                {onToggleDateSort && (
                  <button
                    onClick={onToggleDateSort}
                    title={
                      dateSortOrder === 'newest' ? 'Сортировка по дате: новые сначала → клик для старых' :
                      dateSortOrder === 'oldest' ? 'Сортировка по дате: старые сначала → клик для выкл' :
                      'Сортировать по дате добавления'
                    }
                    style={{
                      flex: '0 0 auto', width: '32px', padding: '0', fontSize: '14px',
                      background: dateSortOrder === 'newest' ? '#1a2f5a' : dateSortOrder === 'oldest' ? '#3a2000' : '#2f2f38',
                      border: '1px solid #555', borderRadius: '6px', cursor: 'pointer', color: '#fff', height: '32px', lineHeight: '1'
                    }}
                  >
                    {dateSortOrder === 'newest' ? '🔽' : dateSortOrder === 'oldest' ? '🔼' : '📅'}
                  </button>
                )}

                <button
                  className={`ctl-btn ${activeTab === 'specialPermissions' ? 'is-active' : ''}`}
                  onClick={() => typeof onTabChange === 'function' && onTabChange('specialPermissions')}
                  title="Устройства со спец. правами"
                  style={{ flex: '1 1 auto' }}
                >
                  Спец раз.
                </button>

                <button
                  className={`ctl-btn ${activeTab === 'inWork' ? 'is-active' : ''}`}
                  onClick={() => typeof onTabChange === 'function' && onTabChange('inWork')}
                  style={{ flex: '1 1 auto' }}
                >
                  В работе
                </button>

                <button
                  className={`ctl-btn ${viewMode === 'newToday' ? 'is-active' : ''}`}
                  onClick={() => { onChangeViewMode('newToday'); if (typeof onTabChange === 'function') onTabChange('devices'); }}
                  title="Устройства, добавленные сегодня"
                  style={{ flex: '1 1 auto' }}
                >
                  Новые
                </button>
            </div>

            <div className="search-panel-wrapper">
              <div
                className="search-panel-header"
                onClick={() => setSearchExpanded(v => !v)}
              >
                <span>Поиск</span>
                <span className="search-panel-arrow">{searchExpanded ? '▲' : '▼'}</span>
              </div>

              {searchExpanded && (
                <div className="search-panel-body">

                  <div className="search-panel-row">
                    <label className="search-panel-label">UUID:</label>
                    <input
                      type="text"
                      className="filter-input search-panel-input"
                      placeholder="UUID (от 4 символов)"
                      value={uuidValue}
                      onChange={(e) => handleFieldChange('uuid', e.target.value)}
                    />
                    {uuidValue && (
                      <button className="search-clear-btn" onClick={() => handleFieldChange('uuid', '')} title="Очистить">✕</button>
                    )}
                  </div>

                  <div className="search-panel-row">
                    <label className="search-panel-label">Заметка:</label>
                    <input
                      type="text"
                      className="filter-input search-panel-input"
                      placeholder="Текст заметки (от 2 символов)"
                      value={noteValue}
                      onChange={(e) => handleFieldChange('note', e.target.value)}
                    />
                    {noteValue && (
                      <button className="search-clear-btn" onClick={() => handleFieldChange('note', '')} title="Очистить">✕</button>
                    )}
                  </div>

                  <div className="search-panel-row">
                    <label className="search-panel-label">Телефон:</label>
                    <input
                      type="text"
                      className="filter-input search-panel-input"
                      placeholder="Номер телефона (от 3 цифр)"
                      value={phoneValue}
                      onChange={(e) => handleFieldChange('phone', e.target.value)}
                    />
                    {phoneValue && (
                      <button className="search-clear-btn" onClick={() => handleFieldChange('phone', '')} title="Очистить">✕</button>
                    )}
                  </div>

                  <div className="search-panel-row">
                    <label className="search-panel-label">Приложение:</label>
                    <input
                      type="text"
                      className="filter-input search-panel-input"
                      placeholder="Приложение (через запятую = ИЛИ)"
                      value={appValue}
                      onChange={(e) => handleFieldChange('app', e.target.value)}
                    />
                    {appValue && (
                      <button className="search-clear-btn" onClick={() => handleFieldChange('app', '')} title="Очистить">✕</button>
                    )}
                  </div>


                  <div className="search-panel-row">
                    <label className="search-panel-label">Оператор:</label>
                    <input
                      type="text"
                      className="filter-input search-panel-input"
                      placeholder="Название оператора (от 2 символов)"
                      value={carrierValue}
                      onChange={(e) => handleFieldChange('carrier', e.target.value)}
                    />
                    {carrierValue && (
                      <button className="search-clear-btn" onClick={() => handleFieldChange('carrier', '')} title="Очистить">✕</button>
                    )}
                  </div>

                  <div className="search-panel-row">
                    <label className="search-panel-label">Сборка:</label>
                    <input
                      type="text"
                      className="filter-input search-panel-input"
                      placeholder="Название APK (app_name, от 2 символов)"
                      value={appNameValue}
                      onChange={(e) => handleFieldChange('appname', e.target.value)}
                    />
                    {appNameValue && (
                      <button className="search-clear-btn" onClick={() => handleFieldChange('appname', '')} title="Очистить">✕</button>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="search-panel-row">
                      <label className="search-panel-label">Польз.:</label>
                      <input
                        type="text"
                        className="filter-input search-panel-input"
                        placeholder="Логин пользователя (локальный фильтр)"
                        value={userValue}
                        onChange={(e) => setUserValue(e.target.value)}
                      />
                      {userValue && (
                        <button className="search-clear-btn" onClick={() => setUserValue('')} title="Очистить">✕</button>
                      )}
                    </div>
                  )}

                  {isGlobalSearch && (
                    <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                      {[
                        { type: 'uuid',  label: 'UUID',        value: uuidValue  },
                        { type: 'note',  label: 'Заметка',     value: noteValue  },
                        { type: 'phone', label: 'Телефон',     value: phoneValue },
                        { type: 'app',     label: 'Приложение', value: appValue     },
                        { type: 'carrier', label: 'Оператор',   value: carrierValue },
                        { type: 'appname', label: 'Сборка',     value: appNameValue },
                      ].filter(f => f.value).map(f => (
                        <span key={f.type} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '2px 6px', borderRadius: '4px', fontSize: '11px',
                          background: f.type === primaryFilterType ? '#1a3a5a' : '#2a1a3a',
                          border: `1px solid ${f.type === primaryFilterType ? '#2a6090' : '#6a2a90'}`,
                          color: '#ccc'
                        }}>
                          <span>{f.type === primaryFilterType ? '🔍' : '✂️'} {f.label}: &ldquo;{f.value}&rdquo;</span>
                          <button onClick={() => handleFieldChange(f.type, '')} style={{
                            background: 'none', border: 'none', cursor: 'pointer', color: '#999',
                            fontSize: '12px', padding: '0 0 0 2px', lineHeight: '1'
                          }}>✕</button>
                        </span>
                      ))}
                      {[uuidValue, noteValue, phoneValue, appValue, carrierValue, appNameValue].filter(Boolean).length >= 2 && primaryFilterCount > 0 && (
                        <span style={{ fontSize: '11px', color: '#667', marginLeft: '4px' }}>
                          {primaryFilterCount} → {totalCount} уст.
                        </span>
                      )}
                      {[uuidValue, noteValue, phoneValue, appValue, carrierValue, appNameValue].filter(Boolean).length === 1 && (
                        <span style={{ fontSize: '11px', color: '#667' }}>Найдено: {totalCount} уст.</span>
                      )}
                      <button className="search-clear-btn" onClick={handleClearAll} title="Сбросить поиск" style={{marginLeft:'4px'}}>✕ Сбросить</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <Pager
              page={currentPage}
              totalPages={totalPages}
              totalDisplay={totalCount}
              onSetPage={onSetPage}
              onRefresh={onRefresh}
              isPageLocked={isPageLocked}
              onTogglePageLock={onTogglePageLock}
              dateSortOrder={dateSortOrder}
            />
            {visibleDevices.length === 0 && !loading && ( 
              <p style={{ color: '#aaaaaa', textAlign: 'center', marginTop: '20px' }}>
                Нет устройств для отображения.
              </p>
            )}

            {visibleDevices.map(device => (
              <DeviceRow
                key={device.uuid || device._id}
                device={device}
                clockTick={clockTick}
                apkState={apkCheckState?.[device.uuid]?.state || 'idle'}
                isMenuOpen={openMenuUuid === device.uuid}
                isSelected={selectedForBulk.has(getDeviceKey(device))}
                isAdmin={isAdmin}
                canTransferDevices={canTransferDevices}
                isBannedView={isGlobalSearch ? device.banned === true : isBannedView}
                isInWorkView={isGlobalSearch ? device.inWork === true : isInWorkView}
                isDeletedApkView={isGlobalSearch ? device.apkDeleted === true : isDeletedApkView}
                isMarkedForDeletionView={isGlobalSearch ? device.markedForDeletion === true : isMarkedForDeletionView}
                onMenuToggle={handleMenuToggle}
                onMenuClose={handleMenuClose}
                onToggleSelect={toggleSelect}
                onSelectDevice={stableSel}
                onTransferDevice={stableTra}
                onBanDevice={stableBan}
                onUnbanDevice={stableUnban}
                onMarkInWork={stableMiw}
                onUnmarkInWork={stableUmiw}
                onMarkForDeletion={stableMfd}
                onUnmarkForDeletion={stableUfd}
                onDeletePermanently={stableDp}
                onSelfDestruct={stableSd}
                onCheckInstall={stableCi}
                onSaveNote={stableSn}
                onSaveManualPhone={stableSmp}
              />
            ))}

            {!isGlobalSearch && (
  <Pager
              page={currentPage}
              totalPages={totalPages}
              totalDisplay={totalCount}
              onSetPage={onSetPage}
              onRefresh={onRefresh}
              isPageLocked={isPageLocked}
              onTogglePageLock={onTogglePageLock}
              dateSortOrder={dateSortOrder}
            />
)}
          </>
        ) : (
          <p>Ошибка: данные об устройствах не являются массивом.</p>
        )}
      </div>
    <InputModal
      isOpen={bulkTransferModal.isOpen}
      title={`Передать ${selectedForBulk.size} устройств`}
      fields={[{ name: 'login', label: 'Логин нового владельца', autoFocus: true }]}
      onSubmit={({ login }) => {
        setBulkTransferModal({ isOpen: false });
        if (login?.trim()) onBulkTransferSelected(Array.from(selectedForBulk), login.trim());
      }}
      onCancel={() => setBulkTransferModal({ isOpen: false })}
    />
    </div>
  );
}

export default DeviceList;
